import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Award, Check, Lock, Loader2, Send } from "lucide-react";

const BLOC_META = {
  C2: { key: "ep2_competencies", label: "Bloc 2 · EP2", prefix: "C2" },
  C3: { key: "ep3_competencies", label: "Bloc 3 · EP3", prefix: "C3" },
};

export default function CapDossierSection({ portfolio }) {
  const [dossier, setDossier] = useState(null);
  const [selection, setSelection] = useState({ C2: [], C3: [] });
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/cap-dossier").then(({ data }) => {
      setDossier(data);
      setSelection({
        C2: data.ep2_competencies || [],
        C3: data.ep3_competencies || [],
      });
    });
  }, []);

  if (!dossier || !portfolio) return null;

  const toggle = (bloc, code) => {
    if (dossier.locked) return;
    setSelection((s) => {
      const cur = s[bloc];
      if (cur.includes(code)) return { ...s, [bloc]: cur.filter((c) => c !== code) };
      if (cur.length >= 4) return s;
      return { ...s, [bloc]: [...cur, code] };
    });
  };

  const bothComplete = selection.C2.length === 4 && selection.C3.length === 4;

  const saveDraft = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/cap-dossier", {
        ep2_competencies: selection.C2, ep3_competencies: selection.C3,
      });
      setDossier(data);
      toast.success("Choix enregistré ✓");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistre d'abord 4 compétences par bloc.");
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/cap-dossier/submit");
      setDossier(data);
      toast.success("Dossier final soumis à ton professeur ✓");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de soumettre pour l'instant.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-8 rounded-3xl bg-white border border-slate-100 shadow-sm p-6" data-testid="cap-dossier-section">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-2xl bg-slate-900 text-white flex items-center justify-center shrink-0">
          <Award className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h2 className="font-display text-xl font-semibold text-slate-900">Mon dossier final CAP (EP2 & EP3)</h2>
          <p className="text-sm text-slate-500 font-medium mt-0.5">
            Choisis 4 compétences par bloc : ce sont celles que ton professeur regroupera pour tes notes finales.
          </p>
        </div>
      </div>

      {dossier.locked && (
        <div className="mt-4 rounded-2xl bg-slate-900 text-white px-4 py-3 flex items-center gap-2.5" data-testid="cap-dossier-locked-banner">
          <Lock className="h-4 w-4 shrink-0" />
          <p className="text-sm font-bold">
            Dossier soumis le {dossier.submitted_at?.slice(0, 10).split("-").reverse().join("/")} — verrouillé. Demande à ton professeur s'il faut le modifier.
          </p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-5 mt-5">
        {Object.entries(BLOC_META).map(([bloc, meta]) => {
          const comps = portfolio.filter((c) => c.code.startsWith(meta.prefix));
          return (
            <div key={bloc} data-testid={`cap-dossier-bloc-${bloc}`}>
              <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400 mb-2">
                {meta.label} · {selection[bloc].length}/4 choisies
              </p>
              <div className="space-y-2">
                {comps.map((c) => {
                  const active = selection[bloc].includes(c.code);
                  const disabled = dossier.locked || (!active && selection[bloc].length >= 4);
                  return (
                    <button
                      key={c.code}
                      onClick={() => toggle(bloc, c.code)}
                      disabled={disabled}
                      data-testid={`cap-dossier-toggle-${c.code}`}
                      className={`w-full text-left rounded-2xl border-2 px-4 py-2.5 flex items-center gap-2.5 transition-all ${
                        active
                          ? "border-orange-500 bg-orange-50"
                          : disabled
                          ? "border-slate-100 bg-slate-50 opacity-50 cursor-not-allowed"
                          : "border-slate-100 hover:border-slate-200"
                      }`}
                    >
                      <span className={`h-5 w-5 rounded-md flex items-center justify-center shrink-0 ${active ? "bg-orange-500 text-white" : "bg-slate-200 text-transparent"}`}>
                        <Check className="h-3.5 w-3.5" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-bold text-slate-800">{c.code} · {c.short}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {!dossier.locked && (
        <div className="flex flex-wrap items-center gap-3 mt-5">
          <button
            onClick={saveDraft}
            disabled={saving || selection.C2.length !== 4 || selection.C3.length !== 4}
            data-testid="cap-dossier-save-btn"
            className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 text-slate-700 rounded-full px-5 py-2.5 font-bold text-sm transition-colors"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Enregistrer mon choix
          </button>
          <button
            onClick={submit}
            disabled={submitting || !bothComplete}
            data-testid="cap-dossier-submit-btn"
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white rounded-full px-5 py-2.5 font-bold text-sm transition-colors shadow-sm shadow-orange-200"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Soumettre définitivement
          </button>
          {!bothComplete && (
            <p className="text-xs text-slate-400 font-medium">Choisis 4 compétences par bloc pour pouvoir soumettre.</p>
          )}
        </div>
      )}
    </div>
  );
}
